import { apiService } from './api';

/** Behavioural role of a payment type.
 *  - Cash   → physical cash; only one active Cash type allowed; triggers change-due UI
 *  - Credit → customer on-account credit; only one active Credit type allowed
 *  - Other  → everything else (card, voucher, bank transfer…); unlimited
 */
export type PaymentCategory = 'Cash' | 'Credit' | 'Other';

export type PaymentUsageScope = 'Sales' | 'Purchase' | 'Both';

export const PAYMENT_CATEGORY_LABELS: Record<PaymentCategory, string> = {
  Cash:   'Cash',
  Credit: 'Credit',
  Other:  'Other',
};

export interface PaymentType {
  id: number;
  code: string;
  name: string;
  description?: string;
  paymentCategory: PaymentCategory;
  usageScope: PaymentUsageScope;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  createdDate: string;
}

export interface CreatePaymentTypeDTO {
  code: string;
  name: string;
  description?: string;
  paymentCategory: PaymentCategory;
  usageScope: PaymentUsageScope;
  isDefault: boolean;
  sortOrder: number;
}

export interface UpdatePaymentTypeDTO {
  code: string;
  name: string;
  description?: string;
  paymentCategory: PaymentCategory;
  usageScope: PaymentUsageScope;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

class PaymentTypeService {
  async getPaymentTypes(pageNumber = 1, pageSize = 100) {
    try {
      const response = await apiService.get<PaginatedResponse<PaymentType>>(
        '/payment-types',
        { pageNumber, pageSize }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payment types:', error);
      throw error;
    }
  }

  async getActivePaymentTypes(scope?: PaymentUsageScope) {
    try {
      const params = scope ? { scope } : {};
      const response = await apiService.get<PaymentType[]>('/payment-types/active', params);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching active payment types:', error);
      throw error;
    }
  }

  async getPaymentTypeById(id: number) {
    try {
      const response = await apiService.get<PaymentType>(`/payment-types/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payment type:', error);
      throw error;
    }
  }

  async createPaymentType(dto: CreatePaymentTypeDTO) {
    try {
      const response = await apiService.post<PaymentType>('/payment-types', dto);
      return response.data.data;
    } catch (error) {
      console.error('Error creating payment type:', error);
      throw error;
    }
  }

  async updatePaymentType(id: number, dto: UpdatePaymentTypeDTO) {
    try {
      const response = await apiService.put<PaymentType>(`/payment-types/${id}`, dto);
      return response.data.data;
    } catch (error) {
      console.error('Error updating payment type:', error);
      throw error;
    }
  }

  async deletePaymentType(id: number) {
    try {
      await apiService.delete(`/payment-types/${id}`);
    } catch (error) {
      console.error('Error deleting payment type:', error);
      throw error;
    }
  }
}

const paymentTypeService = new PaymentTypeService();
export default paymentTypeService;
